/**
 * The audit log's search box (`KLQ-20260821-003` §A.4, §B).
 *
 * **One request per settled term, not per keystroke.** The box holds what is being typed; the
 * URL — and so the filter object, the query key and the request — only learns the term once
 * typing has paused for `SETTLE_MS`. The same 300 ms and the same last-timer-wins idiom as the
 * feed's box (`features/comments/FeedPanel.tsx`), for the same reason: the term is part of the
 * query key, so without the pause every character is a request against the one table that only
 * grows.
 *
 * **The URL is still the state.** A settled term is a navigation through the filter bar's one
 * writer, so a searched log is a link and the CSV export takes the same `q`. It **replaces** the
 * history entry rather than pushing one: *Back* returns to the screen somebody came from, not
 * through every word they typed on the way.
 *
 * **What it searches is said in its own copy.** The server matches codes, level names and version
 * numbers — the event's values — and never a person, because the log carries people as ids and
 * names them only at render time (the spec's Project Context: *"the UI copy should say so, or the
 * box silently under-answers"*). Who did it is the *Who* filter's question.
 */

import { useEffect, useState } from 'react';

import { useMessages } from '@/lib/i18n';

import styles from './Activity.module.css';
import { MAX_SEARCH, searchTerm } from './filters';

/**
 * How long typing has to pause before the term becomes a request. TWIN of `FeedPanel.tsx` and
 * `features/search/useSearchStream.ts`.
 */
const SETTLE_MS = 300;

export interface ActivitySearchBoxProps {
  /** The term the URL holds right now. */
  readonly term: string | null;
  /** Called once per settled term that differs from `term`. */
  readonly onSettle: (next: string | null) => void;
}

export function ActivitySearchBox({ term, onSettle }: ActivitySearchBoxProps) {
  const t = useMessages('activity');
  const [draft, setDraft] = useState(term ?? '');

  // The URL moved without the box — *Clear*, a chip's cross, *Back*. Adjusted during render (the
  // documented alternative to an effect that sets state), and only when the URL now disagrees with
  // what is typed: the box's own settle lands here too, and must not rewrite the draft under the
  // cursor.
  const [held, setHeld] = useState(term);
  if (held !== term) {
    setHeld(term);
    if (searchTerm(draft) !== term) setDraft(term ?? '');
  }

  // The last keystroke wins: every earlier timer is cleared by the cleanup.
  useEffect(() => {
    const next = searchTerm(draft);
    if (next === term) return undefined;
    const timer = window.setTimeout(() => {
      onSettle(next);
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [draft, term, onSettle]);

  return (
    <input
      className={styles.search}
      type="search"
      value={draft}
      maxLength={MAX_SEARCH}
      placeholder={t('search.placeholder')}
      aria-label={t('search.label')}
      onChange={(event) => {
        setDraft(event.currentTarget.value);
      }}
    />
  );
}
